import { jsPDF } from 'jspdf';

// Image imports - these resolve to URLs in the Vite build
import artistMain from '../assets/artist-main.png';
import wthnViewPic from '../assets/WTHNViewEpkPic.png';
import igbr2i2dCover from '../assets/IGBR2I2Dalbumcover.png';
import docuMEANTaryPic from '../assets/docuMEANTary2.png';
import artistTreasure from '../assets/artist-treasure.png';
import tmogfBookPic from '../assets/TMOGFbookEpkPic.png';
import irdt2bBookPic from '../assets/IRDT2BbookEpkPic.png';
import musicBookPic from '../assets/MusicBookBplan.png';
import s365BookPic from '../assets/365SbookEpkPic.png';

/** Convert an image URL to a base64 data URL using canvas, returning dimensions too */
async function imageToBase64(src: string): Promise<{ dataUrl: string; naturalW: number; naturalH: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL('image/png', 0.92),
        naturalW: img.naturalWidth,
        naturalH: img.naturalHeight,
      });
    };
    img.onerror = reject;
    img.src = src;
  });
}

/** Fit an image within a bounding box while preserving its aspect ratio */
function fitImageInBox(
  naturalW: number,
  naturalH: number,
  maxW: number,
  maxH: number
): { w: number; h: number } {
  const imgAspect = naturalW / naturalH;
  const boxAspect = maxW / maxH;
  let w: number;
  let h: number;
  if (imgAspect > boxAspect) {
    // Image is wider than the box — fit to width
    w = maxW;
    h = maxW / imgAspect;
  } else {
    // Image is taller than the box — fit to height
    h = maxH;
    w = maxH * imgAspect;
  }
  return { w, h };
}

/** Draw a colored rectangle */
function drawRect(doc: jsPDF, x: number, y: number, w: number, h: number, color: [number, number, number], alpha = 1) {
  doc.setFillColor(color[0], color[1], color[2]);
  doc.setGState(new doc.GState({ opacity: alpha }));
  doc.rect(x, y, w, h, 'F');
  doc.setGState(new doc.GState({ opacity: 1 }));
}

/** Draw a decorative accent line */
function drawAccentLine(doc: jsPDF, x: number, y: number, w: number, color: [number, number, number]) {
  doc.setFillColor(color[0], color[1], color[2]);
  doc.rect(x, y, w, 0.8, 'F');
}

/** Wrap text to fit within max width and return lines */
function wrapText(doc: jsPDF, text: string, _x: number, maxWidth: number, fontSize: number): string[] {
  doc.setFontSize(fontSize);
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = doc.getStringUnitWidth(testLine) * fontSize;
    if (testWidth > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

export async function generatePressKit(): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageW = 210;
  const pageH = 297;
  const margin = 16;

  // Brand colors
  const CYAN: [number, number, number] = [0, 210, 255];
  const GOLD: [number, number, number] = [255, 195, 0];
  const DARK: [number, number, number] = [12, 12, 16];
  const DARK2: [number, number, number] = [20, 20, 28];
  const GRAY: [number, number, number] = [140, 140, 150];
  const WHITE: [number, number, number] = [255, 255, 255];

  // Load images with dimensions
  const [mainImg, wthnImg, igbrImg, treasureImg, tmogfBookImg, irdt2bBookImg, musicBookImg, s365BookImg] = await Promise.all([
    imageToBase64(artistMain),
    imageToBase64(wthnViewPic),
    imageToBase64(igbr2i2dCover),
    imageToBase64(artistTreasure),
    imageToBase64(tmogfBookPic),
    imageToBase64(irdt2bBookPic),
    imageToBase64(musicBookPic),
    imageToBase64(s365BookPic),
  ]);

  // ==================== PAGE 1: COVER ====================
  // Full dark background
  drawRect(doc, 0, 0, pageW, pageH, DARK);

  // Main artist image - large, centered with breathing room
  const coverMaxW = 130;
  const coverMaxH = 150;
  const coverFit = fitImageInBox(mainImg.naturalW, mainImg.naturalH, coverMaxW, coverMaxH);
  const imgX = (pageW - coverFit.w) / 2;
  const imgY = 20 + (coverMaxH - coverFit.h) / 2;
  doc.addImage(mainImg.dataUrl, 'PNG', imgX, imgY, coverFit.w, coverFit.h);

  // Subtle overlay gradient at bottom of image
  drawRect(doc, imgX, imgY + coverFit.h * 0.6, coverFit.w, coverFit.h * 0.4, DARK, 0.6);

  // Cyan accent strip at top
  drawRect(doc, 0, 0, pageW, 3, CYAN, 0.9);

  // Gold decorative line above title
  const titleY = imgY + coverFit.h;
  drawAccentLine(doc, (pageW - 80) / 2, titleY + 10, 80, GOLD);

  // Artist name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(42);
  doc.setTextColor(WHITE[0], WHITE[1], WHITE[2]);
  doc.text('KILEY KELLY', pageW / 2, titleY + 36, { align: 'center' });

  // Title
  doc.setFontSize(18);
  doc.setTextColor(GOLD[0], GOLD[1], GOLD[2]);
  doc.text('THE MACK REP', pageW / 2, titleY + 46, { align: 'center' });

  // Subtitle
  doc.setFontSize(9);
  doc.setTextColor(CYAN[0], CYAN[1], CYAN[2]);
  doc.text('E L E C T R O N I C   P R E S S   K I T', pageW / 2, titleY + 54, { align: 'center' });

  // Bottom cyan accent strip
  drawRect(doc, 0, pageH - 3, pageW, 3, CYAN, 0.9);

  // Bottom tagline
  doc.setFontSize(7);
  doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
  doc.text('www.themackrep.com', pageW / 2, pageH - 8, { align: 'center' });

  // ==================== PAGE 2: ABOUT & DISCOGRAPHY ====================
  doc.addPage();
  drawRect(doc, 0, 0, pageW, pageH, DARK);

  // Top accent bar
  drawRect(doc, 0, 0, pageW, 2, CYAN, 0.9);

  // Section: ABOUT THE ARTIST
  let y = 18;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(CYAN[0], CYAN[1], CYAN[2]);
  doc.text('ABOUT THE ARTIST', margin, y);

  drawAccentLine(doc, margin, y + 3, 50, GOLD);

  y += 14;

  // Artist photo thumbnail + bio side by side
  const thumbMaxW = 52;
  const thumbMaxH = 65;
  const thumbFit = fitImageInBox(treasureImg.naturalW, treasureImg.naturalH, thumbMaxW, thumbMaxH);
  const thumbX = margin + (thumbMaxW - thumbFit.w) / 2;
  const thumbY = y + (thumbMaxH - thumbFit.h) / 2;
  doc.addImage(treasureImg.dataUrl, 'PNG', thumbX, thumbY, thumbFit.w, thumbFit.h);
  // Thin cyan border around thumbnail
  doc.setDrawColor(CYAN[0], CYAN[1], CYAN[2]);
  doc.setLineWidth(0.4);
  doc.rect(margin, y, thumbMaxW, thumbMaxH, 'S');

  const bioText = 'Kiley Kelly, known as The Mack Rep, was born and raised in South Sacramento, Meadowview, California. At an early age he discovered his passion for music but his rap career was put on hold by an 18-year fight with the system. Now free and more determined than ever, Kiley has channeled all that raw energy into a powerhouse music career that is taking the world by storm. With multiple albums, books, apps, and a documentary under his belt, The Mack Rep is redefining what it means to be an independent artist and entrepreneur.';

  const bioX = margin + thumbMaxW + 6;
  const bioMaxW = pageW - margin - bioX;
  const bioLines = wrapText(doc, bioText, bioX, bioMaxW, 8);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(210, 210, 220);

  const lineHeight = 3.8;
  for (const line of bioLines) {
    doc.text(line, bioX, y + 4);
    y += lineHeight;
  }

  y = Math.max(y, thumbMaxH + 18) + 10;

  // Section: DISCOGRAPHY
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(CYAN[0], CYAN[1], CYAN[2]);
  doc.text('DISCOGRAPHY & WORKS', margin, y);
  drawAccentLine(doc, margin, y + 3, 60, GOLD);
  y += 14;

  // Album grid: 2 columns (middle album removed)
  const albums = [
    { img: wthnImg, title: "I'll Rather Die\nThan Be Broke", tag: 'Debut Album' },
    { img: igbrImg, title: "I'm Gon't Be Rich\nIf I Don't Die", tag: 'Sophomore LP' },
  ];

  const colW = (pageW - margin * 2 - 10) / 2;
  const albumImgH = 36;

  for (let i = 0; i < albums.length; i++) {
    const colX = margin + i * (colW + 10);
    const album = albums[i];

    // Card background
    drawRect(doc, colX, y, colW, albumImgH + 22, DARK2);
    doc.setDrawColor(255, 255, 255);
    doc.setGState(new doc.GState({ opacity: 0.1 }));
    doc.setLineWidth(0.2);
    doc.rect(colX, y, colW, albumImgH + 22, 'S');
    doc.setGState(new doc.GState({ opacity: 1 }));

    // Album image - fit within card preserving aspect ratio
    const albumFit = fitImageInBox(album.img.naturalW, album.img.naturalH, colW - 4, albumImgH);
    const albumImgX = colX + 2 + ((colW - 4) - albumFit.w) / 2;
    const albumImgY = y + 2 + (albumImgH - albumFit.h) / 2;
    doc.addImage(album.img.dataUrl, 'PNG', albumImgX, albumImgY, albumFit.w, albumFit.h);

    // Tag
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(CYAN[0], CYAN[1], CYAN[2]);
    doc.text(album.tag.toUpperCase(), colX + 4, y + albumImgH + 7);

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(WHITE[0], WHITE[1], WHITE[2]);
    const titleLines = album.title.split('\n');
    for (let t = 0; t < titleLines.length; t++) {
      doc.text(titleLines[t], colX + 4, y + albumImgH + 13 + t * 3.2);
    }
  }

  y += albumImgH + 32;

  // Section: BOOKS & MEDIA
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(CYAN[0], CYAN[1], CYAN[2]);
  doc.text('BOOKS & MEDIA', margin, y);
  drawAccentLine(doc, margin, y + 3, 45, GOLD);
  y += 14;

  // Books from the media page (2x2 grid)
  const books = [
    { img: tmogfBookImg, title: 'The Making Of Go Flicks', tag: 'Non-Fiction/Autobiography' },
    { img: irdt2bBookImg, title: "I'll Rather Die Than Be Booked", tag: 'Non-Fiction' },
    { img: musicBookImg, title: "I'm Gon't Be Rich If I Don't Die", tag: 'Non-Fiction' },
    { img: s365BookImg, title: '365Startup Business Devotional', tag: 'Devotional' },
  ];

  const bookColW = (pageW - margin * 2 - 6) / 2;
  const bookRowH = 32;
  const bookImgH = 24;
  const bookGap = 6;

  for (let i = 0; i < books.length; i++) {
    const row = Math.floor(i / 2);
    const col = i % 2;
    const bookX = margin + col * (bookColW + 6);
    const bookY = y + row * (bookRowH + bookGap);
    const book = books[i];

    drawRect(doc, bookX, bookY, bookColW, bookRowH, DARK2);
    doc.setDrawColor(255, 255, 255);
    doc.setGState(new doc.GState({ opacity: 0.1 }));
    doc.setLineWidth(0.2);
    doc.rect(bookX, bookY, bookColW, bookRowH, 'S');
    doc.setGState(new doc.GState({ opacity: 1 }));

    // Book image - fit preserving aspect ratio
    const bookFit = fitImageInBox(book.img.naturalW, book.img.naturalH, bookColW - 8, bookImgH);
    const bookImgX = bookX + 4 + ((bookColW - 8) - bookFit.w) / 2;
    const bookImgY = bookY + 3 + (bookImgH - bookFit.h) / 2;
    doc.addImage(book.img.dataUrl, 'PNG', bookImgX, bookImgY, bookFit.w, bookFit.h);

    // Tag
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(4.5);
    doc.setTextColor(CYAN[0], CYAN[1], CYAN[2]);
    doc.text(book.tag.toUpperCase(), bookX + 4, bookY + bookImgH + 6);

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(WHITE[0], WHITE[1], WHITE[2]);
    const bookTitleLines = wrapText(doc, book.title, bookX + 4, bookColW - 8, 5.5);
    for (let t = 0; t < Math.min(bookTitleLines.length, 2); t++) {
      doc.text(bookTitleLines[t], bookX + 4, bookY + bookImgH + 10 + t * 3);
    }
  }

  // Bottom accent
  drawRect(doc, 0, pageH - 2, pageW, 2, CYAN, 0.9);

  // Page number
  doc.setFontSize(7);
  doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
  doc.text('2', pageW / 2, pageH - 5, { align: 'center' });

  // ==================== PAGE 3: ACHIEVEMENTS & CONTACT ====================
  doc.addPage();
  drawRect(doc, 0, 0, pageW, pageH, DARK);

  // Top accent bar
  drawRect(doc, 0, 0, pageW, 2, CYAN, 0.9);

  y = 18;

  // Section: CAREER HIGHLIGHTS
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(CYAN[0], CYAN[1], CYAN[2]);
  doc.text('CAREER HIGHLIGHTS', margin, y);
  drawAccentLine(doc, margin, y + 3, 55, GOLD);
  y += 14;

  const highlights = [
    { num: '01', label: 'Multiple Studio Albums', desc: 'Debut LP and follow-up albums with critically acclaimed storytelling and raw authenticity.' },
    { num: '02', label: 'Published Author', desc: 'Multiple tap-technology music books that break down lyrics bar-by-bar for fans worldwide.' },
    { num: '03', label: 'Documentary Filmmaker', desc: '"In The Making" docuseries capturing the behind-the-scenes journey from incarceration to the inception of Go Flicks, the record industry and beyond.' },
    { num: '04', label: 'Tech Entrepreneur', desc: 'Creator of Go Flicks (AR social media) and Go Eternal (AI legacy) apps.' },
    { num: '05', label: '18-Year Comeback', desc: 'Overcame an 18-year battle with the system to emerge as a powerful independent voice.' },
  ];

  for (const h of highlights) {
    // Number badge
    drawRect(doc, margin, y - 4, 10, 8, CYAN, 0.15);
    doc.setDrawColor(CYAN[0], CYAN[1], CYAN[2]);
    doc.setLineWidth(0.3);
    doc.rect(margin, y - 4, 10, 8, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(CYAN[0], CYAN[1], CYAN[2]);
    doc.text(h.num, margin + 5, y + 1, { align: 'center' });

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(WHITE[0], WHITE[1], WHITE[2]);
    doc.text(h.label, margin + 14, y + 1);

    // Description
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
    const descLines = wrapText(doc, h.desc, margin + 14, pageW - margin * 2 - 14, 7);
    for (const dl of descLines) {
      y += 4.5;
      doc.text(dl, margin + 14, y + 1);
    }
    y += 9;
  }

  y += 6;

  // Divider
  drawAccentLine(doc, margin, y, pageW - margin * 2, GOLD);
  y += 10;

  // Section: CONTACT & BOOKING
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(CYAN[0], CYAN[1], CYAN[2]);
  doc.text('CONTACT & BOOKING', margin, y);
  drawAccentLine(doc, margin, y + 3, 55, GOLD);
  y += 14;

  // Contact cards in grid
  const contacts = [
    { label: 'BOOKING', email: 'booking@themackrep.com', color: CYAN },
    { label: 'MANAGEMENT', email: 'management@themackrep.com', color: [180, 130, 255] as [number, number, number] },
    { label: 'PRESS & MEDIA', email: 'press@themackrep.com', color: GOLD },
    { label: 'FAN MAIL', email: 'fanmail@themackrep.com', color: [80, 220, 140] as [number, number, number] },
  ];

  const cardW = (pageW - margin * 2 - 6) / 2;
  const cardH = 22;

  for (let i = 0; i < contacts.length; i++) {
    const row = Math.floor(i / 2);
    const col = i % 2;
    const cx = margin + col * (cardW + 6);
    const cy = y + row * (cardH + 4);
    const c = contacts[i];

    drawRect(doc, cx, cy, cardW, cardH, DARK2);
    doc.setDrawColor(c.color[0], c.color[1], c.color[2]);
    doc.setGState(new doc.GState({ opacity: 0.3 }));
    doc.setLineWidth(0.3);
    doc.rect(cx, cy, cardW, cardH, 'S');
    doc.setGState(new doc.GState({ opacity: 1 }));

    // Left color accent
    doc.setFillColor(c.color[0], c.color[1], c.color[2]);
    doc.rect(cx, cy, 1.5, cardH, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(c.color[0], c.color[1], c.color[2]);
    doc.text(c.label, cx + 6, cy + 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(200, 200, 210);
    doc.text(c.email, cx + 6, cy + 14);
  }

  y += (cardH + 4) * 2 + 12;

  // Social links section
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(GOLD[0], GOLD[1], GOLD[2]);
  doc.text('FOLLOW THE MACK REP', pageW / 2, y, { align: 'center' });
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
  doc.text('Facebook  |  Instagram  |  TikTok  |  YouTube', pageW / 2, y, { align: 'center' });
  y += 5;

  doc.setFontSize(6);
  doc.setTextColor(CYAN[0], CYAN[1], CYAN[2]);
  doc.text('@themackrep', pageW / 2, y, { align: 'center' });

  // Bottom section - Website & EPK link
  y = pageH - 35;

  // Dark card with border
  drawRect(doc, margin, y, pageW - margin * 2, 28, DARK2);
  doc.setDrawColor(CYAN[0], CYAN[1], CYAN[2]);
  doc.setGState(new doc.GState({ opacity: 0.2 }));
  doc.setLineWidth(0.3);
  doc.rect(margin, y, pageW - margin * 2, 28, 'S');
  doc.setGState(new doc.GState({ opacity: 1 }));

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(WHITE[0], WHITE[1], WHITE[2]);
  doc.text('THE MACK REP', pageW / 2, y + 17, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(CYAN[0], CYAN[1], CYAN[2]);
  doc.text('www.themackrep.com', pageW / 2, y + 22, { align: 'center' });

  // Bottom accent
  drawRect(doc, 0, pageH - 2, pageW, 2, CYAN, 0.9);

  // Page number
  doc.setFontSize(7);
  doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
  doc.text('3', pageW / 2, pageH - 5, { align: 'center' });

  // Copyright
  doc.setFontSize(5);
  doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
  doc.text('\u00A9 2024 Kiley Kelly \u2014 The Mack Rep. All Rights Reserved.', pageW / 2, pageH - 10, { align: 'center' });

  // Save
  doc.save('The-Mack-Rep-Press-Kit.pdf');
}
